import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { FormAgendamentoConfirmacao } from './Confirmacao';
import { AgendaFormHeader } from './Header';
import { SelectEstab } from './SelectEstab';
import { SelectAtendente } from './SelectAtendente';
import { SelectHorario } from './SelectHorario';
import { SelectServico } from './SelectServico';
import { useMediaQuery } from 'react-responsive';
import { useAppContext } from '../../../../contexts/AppContext';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { filterMenusWithPlan } from '../../../../routes';
import { MENU_WIDTHS } from '../../../../dashboard/menu/Menu';
import { usePlanAccess } from '../../../../plans/usePlanAccess';
import { convertMinutesToHHMM, formatToReal } from '../../../../components/utils';
import { BookingBar, capitalize, formatDuration } from '../../booking/BookingParts';
import { isValidCustomerDocument, useFormHorarioClienteContext } from './Context';

export const AgendaForm = () => {
    const {
        servico,
        servicosExtras,
        estab,
        atendente,
        horario,
        data,
        loading,
        setOpenConfirm,
        openConfirm,
        showHorarios,
        setShowHorarios,
        totalPrice,
        paymentRequirement,
        customerCpfCnpj,
        submitForm,
    } = useFormHorarioClienteContext();
    const isMobile = useMediaQuery({ maxWidth: 800 });
    const { shortMenu, subMenu } = useAppContext();
    const { user } = useAuthContext();
    const { menuShowEntry, resolvedPlanKey, billingStatus } = usePlanAccess();

    const showMenu = useMemo(() => {
        if (!user) return false;
        return filterMenusWithPlan(user, menuShowEntry).length > 1;
    }, [user, resolvedPlanKey, billingStatus, menuShowEntry]);

    const contentOffset = useMemo(() => {
        if (isMobile || !showMenu) return 0;
        const menuWidth = shortMenu ? MENU_WIDTHS.collapsed : MENU_WIDTHS.expanded;
        const subMenuWidth = subMenu ? MENU_WIDTHS.subMenu + MENU_WIDTHS.gap : 0;
        return menuWidth + subMenuWidth + MENU_WIDTHS.offset;
    }, [isMobile, showMenu, shortMenu, subMenu]);

    useEffect(() => {
        if (!atendente?.uid || !servico?._id) {
            setShowHorarios(false);
        }
    }, [atendente?.uid, servico?._id]);

    const step = openConfirm
        ? 'confirm'
        : showHorarios
          ? 'horario'
          : atendente?.uid
            ? 'servico'
            : estab?._id
              ? 'atendente'
              : 'estab';

    const serviceCount = 1 + servicosExtras.length;
    const totalDuration =
        Number(servico?.duration || 0) +
        servicosExtras.reduce((total: number, extra: any) => total + Number(extra?.duration || 0), 0);
    const needsDocument = paymentRequirement?.requiresPayment && paymentRequirement?.requiresCustomerDocument;
    const priceLabel = totalPrice ? `R$ ${formatToReal(totalPrice)}` : '';

    const bar =
        step === 'servico' && servico?._id
            ? {
                  title: priceLabel || `${serviceCount} ${serviceCount === 1 ? 'serviço' : 'serviços'}`,
                  detail: `${serviceCount} ${serviceCount === 1 ? 'serviço' : 'serviços'}${totalDuration ? ` · ${formatDuration(totalDuration)}` : ''}`,
                  action: 'Escolher horário',
                  disabled: false,
                  onClick: () => setShowHorarios(true),
              }
            : step === 'horario' && !!horario
              ? {
                    title: `${capitalize(moment(data).format('ddd, DD/MM'))} às ${convertMinutesToHHMM(horario)}`,
                    detail: priceLabel,
                    action: 'Continuar',
                    disabled: loading,
                    onClick: () => setOpenConfirm(true),
                }
              : step === 'confirm'
                ? {
                      title: priceLabel || 'Agendamento',
                      detail: 'Total',
                      action: 'Confirmar',
                      disabled: loading || (needsDocument && !isValidCustomerDocument(customerCpfCnpj)),
                      onClick: submitForm,
                  }
                : null;

    return (
        <div className={`relative mx-auto w-full max-w-2xl px-0 ${bar ? 'pb-28' : 'pb-6'}`}>
            <AgendaFormHeader step={step} />

            {step === 'estab' && <SelectEstab />}
            {step === 'atendente' && <SelectAtendente />}
            {step === 'servico' && <SelectServico />}
            {step === 'horario' && <SelectHorario />}
            {step === 'confirm' && <FormAgendamentoConfirmacao />}

            {bar && <BookingBar {...bar} busy={loading && step === 'confirm'} offset={contentOffset} />}
        </div>
    );
};
